import { Box } from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';
import { IconComponent } from '@backstage/core-plugin-api';
import React from 'react';
import { useEntityPresentation } from '@backstage/plugin-catalog-react';
import { VirtualChip, isVirtual } from '../input/VirtualPicker';
import { VuidChip, vuidOf } from '../VuidChip';
import {
  ServiceProviderChip,
  isServiceProvider,
} from '../input/ServiceProvidersOnlyPicker';

/**
 * The heading of an entity card: the name of the entity with the icon of what
 * it is in front of it.
 *
 * The icon is asked of the presentation api, the same one every link to the
 * entity goes through, rather than picked from the entity here - see `iconOf`
 * in the app. A card that picked one itself would draw a department with the
 * team icon on its own page while every link to it says otherwise, and the
 * About card, which heads a product as much as a system or an api, would have
 * to keep a table of kinds of its own.
 *
 * Where the api names no icon for a kind, `fallbackIcon` stands in, and where
 * there is none of those either the title stands on its own rather than being
 * pushed aside by an icon that means nothing.
 *
 * Behind the name come its chips: first the vuid of a team or a product,
 * which copies it - it goes with the name, the way it used to be written in
 * brackets after it - then the provider chip of a service provider and the
 * virtual chip of a virtual team or department.
 */
export const EntityCardTitle = (props: {
  entity: Entity;
  title: string;
  fallbackIcon?: IconComponent;
}) => {
  const { Icon } = useEntityPresentation(props.entity);
  const KindIcon = Icon || props.fallbackIcon;
  const vuid = vuidOf(props.entity);
  return (
    <Box display="flex" alignItems="center">
      {KindIcon && <KindIcon fontSize="inherit" />}
      <Box ml={KindIcon ? 1 : 0}>{props.title}</Box>
      {vuid && (
        <Box ml={1} display="inline-flex">
          <VuidChip vuid={vuid} />
        </Box>
      )}
      {/* Right behind the name, the way every org view marks the group. Only
          teams and departments carry the tag, so no other card grows one. */}
      {isServiceProvider(props.entity) && (
        <Box ml={1} display="inline-flex">
          <ServiceProviderChip />
        </Box>
      )}
      {isVirtual(props.entity) && (
        <Box ml={1} display="inline-flex">
          <VirtualChip />
        </Box>
      )}
    </Box>
  );
};
